import React from 'react';
import { motion } from 'framer-motion';
import { FiPackage, FiShield, FiAlertTriangle, FiCheckCircle, FiCode, FiGitBranch, FiTrendingUp, FiSettings } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MotionDiv from '../components/MotionDiv';

const SCA = () => {
  const currentStats = [
    { label: "Total Scans", value: "30", description: "SCA scans completed", icon: <FiPackage /> },
    { label: "Repositories Analyzed", value: "11", description: "repositories under SCA analysis", icon: <FiGitBranch /> },
    { label: "Issues Identified", value: "49", description: "security and compliance issues", icon: <FiAlertTriangle /> },
    { label: "Last Scan", value: "21", description: "hours ago", icon: <FiTrendingUp /> }
  ];

  const coreFeatures = [
    {
      title: "Vulnerability Detection",
      icon: <FiShield />,
      features: [
        "Known Vulnerability Identification: Detection of CVEs in dependencies",
        "Severity Assessment: CVSS-based severity scoring and classification",
        "Exploit Availability: Information about available exploits",
        "Patch Availability: Identification of available patches and updates"
      ]
    },
    {
      title: "License Compliance",
      icon: <FiCheckCircle />,
      features: [
        "License Identification: Automatic identification of component licenses",
        "Compliance Checking: Verification against organizational license policies",
        "License Conflicts: Detection of conflicting license requirements",
        "Legal Risk Assessment: Assessment of legal and compliance risks"
      ]
    },
    {
      title: "Component Quality Analysis",
      icon: <FiCode />,
      features: [
        "Code Quality Metrics: Analysis of component code quality",
        "Maintenance Status: Assessment of component maintenance and support",
        "Community Health: Evaluation of open-source project health",
        "Update Frequency: Monitoring of component update patterns"
      ]
    }
  ];

  const supportedEcosystems = [
    { name: "JavaScript/Node.js", managers: "npm, yarn, pnpm package managers" },
    { name: "Python", managers: "pip, conda, poetry, pipenv package managers" },
    { name: "Java", managers: "Maven, Gradle, SBT build systems" },
    { name: ".NET", managers: "NuGet, PackageReference systems" },
    { name: "Go", managers: "Go modules and vendor directories" },
    { name: "Ruby", managers: "Bundler, gem dependencies" },
    { name: "PHP", managers: "Composer package manager" },
    { name: "Rust", managers: "Cargo package manager" },
    { name: "C/C++", managers: "Conan, vcpkg package managers" }
  ];

  const bestPractices = [
    {
      category: "SCA Implementation",
      practices: [
        "Comprehensive Coverage: Ensure all dependencies are analyzed",
        "Regular Scanning: Implement continuous and scheduled scanning",
        "Policy Definition: Define clear security and license policies",
        "Integration: Integrate SCA into development workflows",
        "Training: Train development teams on SCA best practices"
      ]
    },
    {
      category: "Vulnerability Management",
      practices: [
        "Prioritization: Prioritize vulnerabilities based on risk and impact",
        "Rapid Response: Establish rapid response procedures for critical vulnerabilities",
        "Testing: Test all updates and patches before deployment",
        "Documentation: Maintain comprehensive vulnerability documentation",
        "Communication: Communicate vulnerability status to stakeholders"
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Software Composition Analysis (SCA)" />
        
        {/* Hero Section */}
        <MotionDiv delay={0.1} variant="fadeIn">
          <div className="text-center mb-12">
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              The SCA module in Ana Security provides comprehensive vulnerability detection and management for third-party components and open-source dependencies. It identifies security vulnerabilities, license compliance issues, and quality concerns in software dependencies.
            </p>
            
            <MotionDiv delay={0.2} variant="scale">
              <div className="mb-8">
                <img 
                  src="/screenshots/security-modules/06_sca.png" 
                  alt="SCA Dashboard"
                  className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full max-w-4xl mx-auto hover:shadow-xl transition-shadow duration-300"
                />
              </div>
            </MotionDiv>
          </div>
        </MotionDiv>

        {/* Current Statistics */}
        <MotionDiv delay={0.3} variant="slideLeft">
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              SCA Dashboard - Current Statistics
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {currentStats.map((stat, index) => (
                <MotionDiv key={index} delay={0.4 + index * 0.1} variant="bounce" hover>
                  <div className="text-center p-6 bg-light-hover dark:bg-dark-hover rounded-lg">
                    <div className="text-3xl text-primary-500 mb-3 flex justify-center">
                      {stat.icon}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {stat.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {stat.description}
                    </div>
                  </div>
                </MotionDiv>
              ))}
            </div>

            <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Dashboard Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>New Scan Button:</strong> Orange "New Scan" button for initiating new SCA scans</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Project Filtering:</strong> "All" dropdown for filtering by specific projects</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Repository Links:</strong> Direct access to analyzed repositories</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Scan History:</strong> Comprehensive tracking of all scanning activities</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Core Features */}
        <MotionDiv delay={0.2}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              SCA Core Features
            </h2>
            
            <div className="space-y-8">
              {coreFeatures.map((feature, index) => (
                <div key={index} className="border border-light-border dark:border-dark-border rounded-lg p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-2xl text-primary-500">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {feature.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Supported Ecosystems */}
        <MotionDiv delay={0.3}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Supported Ecosystems
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              SCA provides multi-language support with integration for various package managers:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {supportedEcosystems.map((ecosystem, index) => (
                <div key={index} className="p-4 bg-light-hover dark:bg-dark-hover rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {ecosystem.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {ecosystem.managers}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-primary-500/5 dark:bg-primary-500/10 rounded-lg border border-primary-500/20">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Analysis Scope</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Direct Dependencies:</strong> Analysis of directly declared dependencies</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Transitive Dependencies:</strong> Deep analysis of dependency chains</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Development Dependencies:</strong> Analysis of development-only dependencies</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Runtime Dependencies:</strong> Focus on production runtime dependencies</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Best Practices */}
        <MotionDiv delay={0.4}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              SCA Best Practices
            </h2>
            
            <div className="space-y-8">
              {bestPractices.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.practices.map((practice, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{practice}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Call to Action */}
        <MotionDiv delay={0.5}>
          <div className="text-center bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Secure Your Dependencies?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Start using SCA to identify and fix vulnerabilities in your software dependencies.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MotionDiv delay={0.6} variant="scale" hover>
                <a 
                  href="/ana-actions" 
                  className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
                >
                  <FiCheckCircle className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Use ANA Actions</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Automatically fix dependency vulnerabilities</p>
                </a>
              </MotionDiv>
              
              <MotionDiv delay={0.7} variant="scale" hover>
                <a 
                  href="/dashboard" 
                  className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
                >
                  <FiTrendingUp className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">View Dashboard</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Monitor your security metrics</p>
                </a>
              </MotionDiv>
              
              <MotionDiv delay={0.8} variant="scale" hover>
                <a 
                  href="/integrations" 
                  className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
                >
                  <FiSettings className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Setup Integrations</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Connect your repositories</p>
                </a>
              </MotionDiv>
            </div>
          </div>
        </MotionDiv>
      </motion.div>
    </div>
  );
};

export default SCA;
